// FAQ.tsx
import React, { useState } from 'react';
import { Box, Typography, IconButton, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { Homeimages } from '../../assets'; // ✅ Assume `bluelogo` is defined here
import Marquee from 'react-fast-marquee';

type FAQItem = {
  question: string;
  answer: string;
};

const faqData: FAQItem[] = [
  {
    question: 'What can mentoring do for you?',
    answer:
      'Mentoring empowers you with clarity, confidence, and proven sales systems to navigate complex challenges. It accelerates growth by sharpening your negotiation, communication, and closing skills.',
  },
  {
    question: 'Why is mentoring essential?',
    answer:
      'In a competitive sales environment, strategy matters more than effort. Mentoring helps you avoid costly mistakes, master high-trust selling, and achieve sustainable professional success.',
  },
  {
    question: 'Who is mentoring for?',
    answer:
      'Our mentoring programs are designed for sales professionals, consultants, and entrepreneurs seeking structure, confidence, and consistent performance growth.',
  },
  {
    question: 'When is the right time to approach a mentor?',
    answer:
      'The right time is when you’re ready to grow beyond routine results. Whether you’re stuck, scaling, or seeking clarity, mentoring helps you progress with direction and accountability.',
  },
  {
    question: 'How can you find the right mentor?',
    answer:
      'Choose a mentor with real-world experience, proven success stories, and personalised strategies. Rajiv’s two-decade track record in luxury sales makes him a trusted name in professional mentoring.',
  },
  {
    question: 'Where can you find the right mentor?',
    answer:
      'Begin with a discovery call through rajivWilliams.com. Rajiv Williams offers both in-person and online mentoring options, accessible to professionals across India and globally.',
  },
  {
    question: 'Which professionals do you provide mentoring for?',
    answer:
      'Rajiv Williams mentors sales consultants, corporate teams, and service professionals aiming to enhance conversions, confidence, and client communication.',
  },
  {
    question: 'How can someone sign up for the mentoring program?',
    answer:
      'Simply visit the official website and fill out the application form. Our team will schedule your discovery session and guide you through the onboarding process.',
  },
  {
    question:
      'What are the prerequisites for signing up for the mentoring program?',
    answer:
      'All you need is an open mindset, commitment to consistent learning, and a passion for professional growth—no prior experience required.',
  },
  {
    question: 'Do you provide online mentoring?',
    answer:
      'Yes. Rajiv Williams offers flexible online mentoring through live video sessions, interactive exercises, and personalised guidance—ideal for professionals worldwide.',
  },
  {
    question: 'How is progress measured during mentoring?',
    answer:
      'Your growth is tracked through goal reviews, feedback sessions, and measurable improvements across communication, sales, and mindset.',
  },
  {
    question: 'What results can I expect from mentoring?',
    answer:
      'Expect sharper clarity, improved conversions, stronger personal branding, and a confident approach to handling clients in high-value sales environments.',
  },
];

const marqueeWords = [
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
  "FAQ'S",
];

const FAQ: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        padding: '20px',
        backgroundColor: '#f0f0f0',
      }}
    >
      {/* Fixed, Centered Background Image */}
      <Box
        component="img"
        src={Homeimages.bluelogo}
        alt="Best Real Estate Mentor in Hyderabad"
        sx={{
          position: 'fixed', // Fix it on the screen
          top: '50%', // Center it vertically
          left: '50%', // Center it horizontally
          transform: 'translate(-50%, -50%)', // Precisely center it
          width: '100%', // Adjust width as needed
          height: '100%', // Adjust height as needed
          opacity: 0.1, // Keep opacity low
          objectFit: 'contain', // Maintain aspect ratio
          zIndex: 0, // Keep it in the background
          pointerEvents: 'none', // Avoid interfering with other elements
        }}
      />
      <Box sx={{ mt: 12, py: 4 }}>
        <Marquee gradient={false} speed={80} style={{ overflow: 'hidden' }}>
          {marqueeWords.map((word, index) => (
            <Typography
              className="cursor-hover-target"
              key={index}
              variant="h2"
              component="span"
              sx={{
                fontSize: { xs: '40px', md: '40px' },
                fontFamily: 'GilroyBold, sans-serif',
                color: index % 2 === 0 ? '#0f63a5' : 'transparent',
                WebkitTextStroke: index % 2 === 0 ? '0px' : '1px #0f63a5',
                textTransform: 'uppercase',
                mx: 3,
                whiteSpace: 'nowrap',
                fontWeight: 700,
              }}
            >
              {word}
            </Typography>
          ))}
        </Marquee>
      </Box>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Box>
          {faqData.map((faq, index) => {
            const isActive = activeIndex === index;

            return (
              <Box
                key={index}
                onClick={() => toggleFAQ(index)} // Clicking anywhere toggles
                sx={{
                  position: 'relative',
                  backgroundColor: isActive ? '#fff' : 'transparent',
                  border: '1px solid #9fa4a8',
                  borderRadius: '10px',
                  padding: '30px',
                  overflow: 'hidden',
                  transition: '0.3s ease',
                  mb: 3,
                  boxShadow: isActive
                    ? '0 3px 6px rgba(0,0,0,0.1), 0 3px 6px rgba(0,0,0,0.1)'
                    : 'none',
                  cursor: 'pointer',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    marginRight: '35px',
                    position: 'relative',
                    zIndex: 1,
                    color: 'primary.main',
                  }}
                >
                  {faq.question}
                </Typography>

                {isActive && (
                  <Typography sx={{ mt: 3, position: 'relative', zIndex: 1 }}>
                    {faq.answer}
                  </Typography>
                )}

                {/* Toggle Button still works independently */}
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation(); // prevent parent click
                    toggleFAQ(index);
                  }}
                  sx={{
                    backgroundColor: isActive ? '#0f63a5' : 'transparent',
                    position: 'absolute',
                    top: '30px',
                    right: '30px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    zIndex: 2,
                    '&:hover': {
                      backgroundColor: '#0f63a5',
                    },
                  }}
                >
                  {isActive ? (
                    <CloseIcon sx={{ color: 'white', fontSize: '20px' }} />
                  ) : (
                    <ExpandMoreIcon
                      sx={{ fontSize: '20px', color: '#0f63a5' }}
                    />
                  )}
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Container>
    </Box>
  );
};

export default FAQ;
